import { AbstractControl } from '@angular/forms';
import { Guid } from 'guid-typescript';
import { attempt, chain, forEach, isError, isNil, join, replace, startsWith, toLower, trim } from 'lodash';
import * as moment from 'moment';
import { NUMBER_FORMAT, Strings } from './constants';

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean;
  }
}

export function formatBuildingAddress(consumer: any): string {
  let address = [];

  if (trim(consumer.district).length > 0) {
    address.push(trim(consumer.district));
    address.push(', ');
  }

  if (trim(consumer.houseNumber).length > 0 || trim(consumer.street).length > 0 || trim(consumer.locationInfo).length > 0) {
    address.push(trim(consumer.houseNumber));
    address.push(' ');
    address.push(trim(consumer.street));
    address.push(' ');
    address.push(trim(consumer.locationInfo));
    address.push(', ');
  }

  if (trim(consumer.zipCode).length > 0 || trim(consumer.regionName).length > 0) {
    address.push(trim(consumer.zipCode));
    address.push(' ');
    address.push(trim(consumer.regionName));
  }

  address = address.filter((c) => c.length > 0);

  return join(address, '');
}

export function isEmailValid(text: string): boolean {
  const re = new RegExp(Strings.emailRegex);
  return re.test(toLower(text));
}

export function isMobileValid(text: string) {
  const re = new RegExp(Strings.mobileRegex);
  return re.test(text);
}

export function createArray2pipe(list: any, charset?: string) {
  const bom = '\uFEFF';
  const rows = list.map((data: any) => data.join('|')).join('\r\n');
  const type = charset ? 'text/csv'.concat(`;charset=${charset}`) : 'text/csv';
  return new Blob([bom + rows], { type });
}

export function escapeJson2csv(value: any) {
  const text = value.toString().replace(/"/g, `""`);
  return isNil(value) ? '' : `"${text}"`;
}

export function emailValidator(control: AbstractControl) {
  const regex = new RegExp(Strings.emailRegex);

  if (regex.test(control.value)) {
    return null;
  }

  return { email: true };
}

export function sanitizeCsvText(text: string, extraChars: RegExp[] = []) {
  const startsWithBlocklist = ['=', '+', '-', '@'];
  forEach(startsWithBlocklist, (char) => {
    if (startsWith(trim(text), char)) {
      text = replace(text, char, '');
    }
  });
  return text;
}

export function removeExtraSpace(text: string) {
  text = text.replace(/\s+/g, ' ').trim();

  return text;
}

export function uppercaseValidator(control: AbstractControl) {
  const regex = new RegExp('(?=.*[A-Z])');

  if (regex.test(control.value)) {
    return null;
  }

  return { uppercase: true };
}

export function specialCharacterValidator(control: AbstractControl) {
  const regex = new RegExp('^[^<>äöüÄÖÜß]+$');

  if (regex.test(control.value)) {
    return null;
  }

  return { specialcharacter: true };
}

export function lowercaseValidator(control: AbstractControl) {
  const regex = new RegExp('(?=.*[a-z])');

  if (regex.test(control.value)) {
    return null;
  }

  return { lowercase: true };
}

export function numericValidator(control: AbstractControl) {
  const regex = new RegExp('(?=.*[0-9])');

  if (regex.test(control.value)) {
    return null;
  }

  return { numeric: true };
}

export function setDecimalPlaces(data: any) {
  const mappedValues = data.map((x: any) => x);
  const maxValue = Math.max(...mappedValues);
  let precision, decimal;
  if (maxValue < 0.01) {
    precision = Math.ceil(maxValue) / 1000;
    decimal = 3;
  } else if (maxValue < 0.1) {
    precision = Math.ceil(maxValue) / 100;
    decimal = 2;
  } else if (maxValue < 10) {
    precision = Math.ceil(maxValue) / 10;
    decimal = 1;
  } else {
    precision = null;
    decimal = 0;
  }
  return {
    precision: precision,
    decimal: decimal,
  };
}

export function getDecimals(value: any) {
  if (isNaN(value) || Math.floor(value) === value) {
    return 0;
  }
  const decimal = value.toString().split('.')[1].length;
  return (decimal > 0 && decimal < NUMBER_FORMAT.DECIMAL_PLACES ? NUMBER_FORMAT.DECIMAL_PLACES : decimal) || 0;
}

export function fifteenMinuteConverter(text: string) {
  const str = text.split(':');
  let hour: number = parseInt(str[0], 10);
  let minute: number = parseInt(str[1], 10);
  minute = (minute + 45) % 60;

  if (minute === 45) {
    hour = (hour + 23) % 24;
  }

  minute === 0 ? (str[1] = '00') : (str[1] = minute.toString());
  hour < 10 ? (str[0] = '0' + hour) : (str[0] = hour.toString());

  return str[0] + ':' + str[1] + '-' + text;
}

export function saveFile(blob: Blob, fileName: string) {
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, fileName);
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('target', '_blank');
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export function generateGUID() {
  return Guid.create().toString();
}

export function isJsonString(jsonString: string | null) {
  if (isNil(jsonString)) {
    return false;
  }
  return !isError(attempt(JSON.parse, jsonString));
}

export function hasEmptyDataset(datasets: any[]) {
  return (
    chain(datasets)
      .flatMap('data')
      .map((x) => (isNil(x) ? 0 : Math.abs(x)))
      .sum()
      .value() === 0
  );
}

export function getHoursAndMinutesFromUtcDate(date: Date) {
  return moment(date).utc().format('HH:mm');
}

export function getHoursAndMinutesFromDate(date: Date) {
  return moment(date).format('HH:mm:ss');
}

export function getDateFromMinutes(minutes: number) {
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return new Date(0, 0, 0, hh, mm);
}

export function listToTree(list: any, itemIdProp: string, parentIdProp: string, transformFn: (item: any, parent: any | null) => {}) {
  const mapper: { [key: string]: any } = {};
  let node: any;
  const roots = [];
  let i: number;

  for (i = 0; i < list.length; i += 1) {
    mapper[list[i][itemIdProp]] = i;
    list[i].children = [];
  }

  for (i = 0; i < list.length; i += 1) {
    node = list[i];
    if (!isNil(node[parentIdProp])) {
      if (!isNil(mapper[node[parentIdProp]])) {
        if (isNil(transformFn)) {
          list[mapper[node[parentIdProp]]].children.push(node);
        } else {
          list[mapper[node[parentIdProp]]].children.push(transformFn(node, list[mapper[node[parentIdProp]]]));
        }
      }
    } else {
      if (isNil(transformFn)) {
        roots.push(node);
      } else {
        roots.push(transformFn(node, null));
      }
    }
  }

  return roots;
}

export function isBetweenDates(eventDate: any, from: moment.MomentInput, to: moment.MomentInput) {
  return moment(eventDate).isBetween(from, to, 'minute', '(]');
}

export function isIE() {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');
  const trident = ua.indexOf('Trident/');

  return msie > 0 || trident > 0;
}

export const filterByWildCard = (query?: string, ...values: string[]): boolean => {
  if (!query || !query.trim()) {
    return true;
  }

  const normalizedQuery = query.trim().toLowerCase();

  return values.some((value) => value.toLowerCase().includes(normalizedQuery));
};

type UncapitalizedDeep<T> = {
  [K in keyof T as K extends string ? Uncapitalize<K> : never]: T[K] extends Array<infer U>
    ? UncapitalizedDeep<U>[]
    : T[K] extends object
    ? UncapitalizedDeep<T[K]>
    : T[K];
};

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function uncapitalizeKeys<T extends object>(obj: T): UncapitalizedDeep<T> {
  const result: Partial<Record<string, unknown>> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const uncapitalizedKey = key.charAt(0).toLowerCase() + key.slice(1);
      const value = obj[key as keyof T];

      if (Array.isArray(value)) {
        result[uncapitalizedKey] = value.map((item) => (isPlainObject(item) ? uncapitalizeKeys(item) : item));
      } else if (isPlainObject(value)) {
        result[uncapitalizedKey] = uncapitalizeKeys(value);
      } else {
        result[uncapitalizedKey] = value;
      }
    }
  }

  return result as UncapitalizedDeep<T>;
}

export function uncapitalizeArray<T extends object>(arr: T[]): UncapitalizedDeep<T>[] {
  return arr.map(uncapitalizeKeys);
}

export function formatDEDecimalString(readingResult: string | number, pre: string, post: string): string | number {
  let formattedDEDecimal = readingResult;
  if (typeof readingResult === 'number') {
    const decimalsPre = parseInt(pre ?? '0', 10);
    const decimalsPost = parseInt(post ?? '1', 10);

    const minFractionDigits = Math.min(decimalsPre, decimalsPost);
    const maxFractionDigits = Math.max(decimalsPre, decimalsPost);

    formattedDEDecimal = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
    }).format(readingResult);
  }
  return formattedDEDecimal;
}
